import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";

export const VerifiedBadge = ({ verification, testId }) => {
  if (!verification?.verified) return null;
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800">
      <ShieldCheck className="h-3 w-3" />Verified against source {fmtDate(verification.checked_at)}
    </span>
  );
};

const List = ({ title, items, render, testId }) => items.length > 0 && (
  <section data-testid={testId}>
    <h4 className="text-sm font-bold text-slate-900">{title} ({items.length})</h4>
    <ul className="mt-2 space-y-2 text-sm">{items.map((it, i) => <li key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3">{render(it)}</li>)}</ul>
  </section>
);

export const VerifyResultDialog = ({ result, onOpenChange }) => (
  <Dialog open={!!result} onOpenChange={onOpenChange}>
    <DialogContent data-testid="verify-result-dialog" className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
      {result && (
        <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl font-extrabold tracking-tight">
              {result.verified ? <ShieldCheck className="h-6 w-6 text-emerald-600" /> : <ShieldAlert className="h-6 w-6 text-amber-600" />}
              {result.verified ? "Every term and definition matches the source" : "Differences found"}
            </DialogTitle>
            <DialogDescription data-testid="verify-result-summary">
              {result.matched} of {result.total} entries match word for word · {result.changed_count} changed · {result.missing_count} missing from source · {result.new_count} new on source page · {result.pages.length} page{result.pages.length === 1 ? "" : "s"} checked
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <List title="Definition changed on the source page" items={result.changed} testId="verify-changed"
              render={(c) => <><b>{c.term}</b><p className="mt-1 text-red-700 line-through">{c.ours}</p><p className="mt-1 text-emerald-800">{c.source}</p></>} />
            <List title="In this pack but no longer on the source page" items={result.missing} testId="verify-missing" render={(m) => <b>{m.term}</b>} />
            <List title="On the source page but not in this pack" items={result.new_in_source} testId="verify-new" render={(n) => <b>{n.term}</b>} />
            {!result.verified && <p className="text-sm text-slate-600">To fix: re-import from the source page (Import → Pressbooks URL → Replace), then verify again. A pack can only be published after a clean verification.</p>}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const VerifyButton = ({ pack, onResult }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/packs/${pack.id}/verify`);
      toast[data.verified ? "success" : "warning"](data.verified ? "Verified: all entries match the source" : "Differences found — see details");
      onResult(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" variant="outline" className="rounded-lg" disabled={busy} onClick={run} data-testid={`admin-pack-verify-${pack.id}`}>
      <ShieldCheck className="mr-1 h-3.5 w-3.5" />{busy ? "Checking source…" : "Verify against source"}
    </Button>
  );
};
